import React, { useState, useEffect, useRef } from "react";

interface CountdownProps {
    expiryTime: number | null; // Timestamp in milliseconds when countdown expires
    onExpire?: () => void; // Callback when countdown reaches 0
    format?: "minutes" | "seconds" | "mm:ss" | "hh:mm:ss"; // Format to display
    className?: string;
}

const Countdown: React.FC<CountdownProps> = ({
    expiryTime,
    onExpire,
    format = "minutes",
    className = "",
}) => {
    const [remainingTime, setRemainingTime] = useState<number>(0);
    const hasExpiredRef = React.useRef<boolean>(false);

    useEffect(() => {
        if (!expiryTime) {
            setRemainingTime(0);
            hasExpiredRef.current = false;
            return;
        }

        // Reset expired flag when expiryTime changes
        hasExpiredRef.current = false;

        const updateTimer = () => {
            const now = Date.now();
            const remaining = Math.max(0, expiryTime - now);
            setRemainingTime(remaining);

            if (remaining === 0 && onExpire && !hasExpiredRef.current) {
                hasExpiredRef.current = true;
                onExpire();
            }
        };

        // Update immediately
        updateTimer();

        // Update every second
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [expiryTime, onExpire]);

    const formatTime = (): string => {
        if (remainingTime <= 0) {
            return "0";
        }

        const totalSeconds = Math.floor(remainingTime / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        switch (format) {
            case "minutes":
                return Math.ceil(totalSeconds / 60).toString();
            case "seconds":
                return totalSeconds.toString();
            case "mm:ss":
                return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
            case "hh:mm:ss":
                return `${hours.toString().padStart(2, "0")} : ${minutes.toString().padStart(2, "0")} : ${seconds.toString().padStart(2, "0")}`;
            default:
                return Math.ceil(totalSeconds / 60).toString();
        }
    };

    return <span className={className}>{formatTime()}</span>;
};

export default Countdown;

